/** Origin-local IndexedDB authority; no heap fallback and no eviction of admitted work. */
export interface JournalRecord {
  ownerId: string;
  projectId: string;
  operationId: string;
  operation: Record<string, unknown>;
  timestamp: number;
  bytes: number;
}

export class OperationJournalError extends Error {
  constructor(message: string, public readonly admitted = false, public readonly cause?: unknown) {
    super(message);
    this.name = 'OperationJournalError';
  }
}

export const journalKey = (record: Pick<JournalRecord, 'ownerId' | 'projectId' | 'operationId'>): string =>
  JSON.stringify([record.ownerId, record.projectId, record.operationId]);

export class OperationJournal {
  constructor(private readonly options: {
    databaseName?: string; factory?: IDBFactory; maxCount?: number; maxBytes?: number;
  } = {}) {}

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const factory = this.options.factory ?? globalThis.indexedDB;
      if (!factory) { reject(new OperationJournalError('IndexedDB unavailable; change was not admitted')); return; }
      let request: IDBOpenDBRequest;
      try { request = factory.open(this.options.databaseName ?? 'project-manager-operation-journal-v1', 1); }
      catch (cause) { reject(new OperationJournalError('Cannot open operation journal', false, cause)); return; }
      let blocked = false;
      request.onblocked = () => {
        blocked = true;
        reject(new OperationJournalError('Operation journal upgrade is blocked; close older tabs and retry'));
      };
      request.onerror = () => reject(new OperationJournalError('Cannot open operation journal', false, request.error));
      request.onupgradeneeded = () => {
        const store = request.result.createObjectStore('operations', { keyPath: ['ownerId', 'projectId', 'operationId'] });
        store.createIndex('owner', 'ownerId');
      };
      request.onsuccess = () => {
        if (blocked) { request.result.close(); return; }
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
    });
  }

  private async transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore, result: (value: T) => void, fail: (error: Error) => void) => void): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      let tx: IDBTransaction;
      try { tx = db.transaction('operations', mode, mode === 'readwrite' ? { durability: 'strict' } : undefined); }
      catch (cause) { db.close(); reject(new OperationJournalError('Cannot start operation journal transaction', false, cause)); return; }
      let value: T;
      let failure: Error | undefined;
      tx.oncomplete = () => { db.close(); resolve(value); };
      tx.onabort = () => {
        db.close();
        reject(failure ?? new OperationJournalError('Operation journal transaction aborted', false, tx.error));
      };
      tx.onerror = () => { /* Default IndexedDB request error aborts the entire transaction. */ };
      const fail = (error: Error) => { failure = error; tx.abort(); };
      try { action(tx.objectStore('operations'), result => { value = result; }, fail); }
      catch (cause) { fail(new OperationJournalError('Operation journal storage failed', false, cause)); }
    });
  }

  async admit(input: Omit<JournalRecord, 'bytes'>): Promise<JournalRecord> {
    if (!input.ownerId || !input.projectId || input.projectId === 'default' || !input.operationId || input.operation.id !== input.operationId) {
      throw new OperationJournalError('Explicit owner, project and matching stable operation ID are required');
    }
    // Capture exact JSON transport semantics now, before any asynchronous storage work.
    let record: JournalRecord;
    try {
      // Only the operation and non-secret identity metadata enter storage. Business
      // data is opaque: a custom field named token/password remains exact user data.
      const json = JSON.stringify({ ownerId: input.ownerId, projectId: input.projectId,
        operationId: input.operationId, operation: input.operation, timestamp: input.timestamp });
      record = { ...JSON.parse(json), bytes: new TextEncoder().encode(json).byteLength + 32 };
    } catch (cause) { throw new OperationJournalError('Operation cannot be journaled safely', false, cause); }
    return this.transaction('readwrite', (store, result, fail) => {
      // The shared readwrite lock serializes limits and identity checks across tabs.
      const request = store.getAll();
      request.onsuccess = () => {
        const records = request.result as JournalRecord[];
        if (records.some(existing => journalKey(existing) === journalKey(record))) {
          fail(new OperationJournalError('Operation identity is already admitted; reconcile before resubmitting')); return;
        }
        if (records.length >= (this.options.maxCount ?? 1000) ||
          records.reduce((bytes, item) => bytes + item.bytes, 0) + record.bytes > (this.options.maxBytes ?? 10 * 1024 * 1024)) {
          fail(new OperationJournalError('Operation journal is full; change was not admitted (no work was pruned)')); return;
        }
        try { store.add(record); result(record); }
        catch (cause) { fail(new OperationJournalError('Operation journal write failed', false, cause)); }
      };
    });
  }

  listOwner(ownerId: string): Promise<JournalRecord[]> {
    return this.transaction('readonly', (store, result) => {
      const request = store.index('owner').getAll(ownerId);
      request.onsuccess = () => result(request.result as JournalRecord[]);
    });
  }

  removeCommitted(record: JournalRecord): Promise<void> {
    return this.transaction('readwrite', (store, result, fail) => {
      const key = [record.ownerId, record.projectId, record.operationId];
      const request = store.get(key);
      request.onsuccess = () => {
        const current = request.result as JournalRecord | undefined;
        if (current && JSON.stringify(current) !== JSON.stringify(record)) {
          fail(new OperationJournalError('Journal identity changed during receipt readback', true)); return;
        }
        try { store.delete(key); result(undefined); }
        catch (cause) { fail(new OperationJournalError('Cannot clear committed journal record', true, cause)); }
      };
    });
  }
}

export interface ReceiptQueryResult {
  receipts: Array<{ operationId: string; projectId: string; outcome: 'committed' | 'conflict' | 'partial' | 'rejected'; requestHash: string; committedAt: string | null }>;
  missing: string[];
}

/** A partial, duplicate, foreign or malformed response proves nothing, including absence. */
export function validateReceiptQuery(value: unknown, records: JournalRecord[]): ReceiptQueryResult {
  const invalid = () => new OperationJournalError('Invalid operation receipt response; work remains unresolved', true);
  if (!value || typeof value !== 'object') throw invalid();
  const envelope = value as { success?: unknown; protocol?: unknown; data?: ReceiptQueryResult };
  if (envelope.protocol !== 'ws-receipts-v1' || envelope.success !== true || !envelope.data || !Array.isArray(envelope.data.receipts) || !Array.isArray(envelope.data.missing)) throw invalid();
  const expected = new Map(records.map(record => [record.operationId, record.projectId]));
  const seen = new Set<string>();
  for (const receipt of envelope.data.receipts) {
    if (!receipt || !expected.has(receipt.operationId) || seen.has(receipt.operationId) || expected.get(receipt.operationId) !== receipt.projectId ||
      !['committed', 'conflict', 'partial', 'rejected'].includes(receipt.outcome) || typeof receipt.requestHash !== 'string' || !receipt.requestHash ||
      (receipt.outcome === 'committed' && (typeof receipt.committedAt !== 'string' || !receipt.committedAt))) throw invalid();
    seen.add(receipt.operationId);
  }
  for (const id of envelope.data.missing) {
    if (typeof id !== 'string' || !expected.has(id) || seen.has(id)) throw invalid();
    seen.add(id);
  }
  if (seen.size !== expected.size) throw invalid();
  return envelope.data;
}
